import { Bar, BarChart, CartesianGrid, Cell, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Lightbulb } from "lucide-react";

const MES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
const MES_FULL = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
const pct = (a, b) => (b ? Math.round((a / b) * 1000) / 10 : 0);

function Trend({ d, onMonthClick }) {
  const last = Math.max(0, ...Object.keys(d.month_totals).map(Number));
  const sel = d.mes;
  const data = MES.map((label, i) => ({
    label,
    cur: i + 1 <= last ? d.month_totals[i + 1] || 0 : null,
  }));
  return (
    <div className="surface p-5 lg:col-span-2 [&_*:focus]:outline-none" data-testid="zonas-hot-trend">
      <h2 className="text-base md:text-lg font-black font-display">Tendencia mensual</h2>
      <p className="text-[12px] text-gray-500 font-semibold mb-3">Unidades por mes · {d.year} · toca una barra para filtrar ese mes{d.mes ? " (tócala de nuevo para quitarlo)" : ""}</p>
      <div style={{ height: 260 }}>
        <ResponsiveContainer>
          <BarChart data={data} margin={{ top: 22, right: 8, left: -18, bottom: 0 }} style={{ cursor: "pointer" }}
                    onClick={(e) => {
                      const i = Number(e?.activeIndex ?? MES.indexOf(e?.activeLabel));
                      if (i >= 0 && i < 12 && onMonthClick) onMonthClick(i + 1);
                    }}>
            <CartesianGrid stroke="#f1f5f9" vertical={false}/>
            <XAxis dataKey="label" tick={{ fontSize: 11, fontWeight: 700 }} axisLine={false} tickLine={false}/>
            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} axisLine={false} tickLine={false}/>
            <Tooltip cursor={{ fill: "#fff7ed" }}/>
            <Bar dataKey="cur" name={String(d.year)} fill="#ea580c" radius={[4, 4, 0, 0]}>
              <LabelList dataKey="cur" position="top" style={{ fontSize: 12, fontWeight: 900, fill: "#7c2d12" }} formatter={(v) => (v ? v : "")}/>
              {data.map((_, i) => <Cell key={i} fillOpacity={sel && sel !== i + 1 ? 0.35 : 1}/>)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function Ranking({ d }) {
  const rows = [...d.groups].sort((a, b) => b.total - a.total);
  return (
    <div className="surface p-5" data-testid="zonas-hot-ranking">
      <h2 className="text-base md:text-lg font-black font-display">Ranking de sucursales</h2>
      <p className="text-[12px] text-gray-500 font-semibold mb-4">Unidades {d.mes ? `${MES[d.mes - 1]} ` : ""}{d.year} y peso en el total</p>
      <div className="space-y-3">
        {rows.map((g, i) => (
          <div key={g.sucursal} data-testid={`zonas-hot-rank-${g.sucursal}`}>
            <div className="flex justify-between text-[13px] font-bold">
              <span><span className="text-gray-400 mr-2">{i + 1}</span>{g.sucursal}</span>
              <span>{g.total} <span className="text-gray-400 font-semibold">· {pct(g.total, d.total)}%</span></span>
            </div>
            <div className="h-2 rounded-full bg-orange-50 mt-1 overflow-hidden">
              <div className="h-full rounded-full bg-orange-500 transition-[width] duration-700" style={{ width: `${pct(g.total, rows[0].total)}%` }}/>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const findings = (d, inv) => {
  const out = [];
  const top = [...d.groups].sort((a, b) => b.total - a.total)[0];
  if (top) out.push(`${top.sucursal} concentra el ${pct(top.total, d.total)}% de las unidades (${top.total} de ${d.total}).`);
  const prevSame = d.prev_same || 0;
  if (prevSame && d.last_month) {
    const ch = Math.round(((d.total - prevSame) / prevSame) * 1000) / 10;
    const period = d.mes ? `En ${MES_FULL[d.mes - 1]}` : `De enero a ${MES_FULL[d.last_month - 1]}`;
    out.push(`${period} se vendieron ${d.total} unidades vs ${prevSame} en ${d.prev_year} (${ch >= 0 ? "+" : ""}${ch}%).`);
  }
  const best = [...d.months].sort((a, b) => (d.month_totals[b.n] || 0) - (d.month_totals[a.n] || 0))[0];
  if (best && !d.mes) out.push(`${best.label} fue el mejor mes con ${d.month_totals[best.n]} unidades; promedio mensual de ${d.promedio}.`);
  if (inv?.with_data === 1) out.push(`La única unidad con dato llevaba ${inv.avg_days} días en inventario al facturar.`);
  else if (inv?.with_data) out.push(`Las ${inv.with_data} unidades con dato llevaban en promedio ${inv.avg_days} días en inventario al facturar; ${inv.over_365} superaban los 365 días.`);
  return out;
};

export default function ZonasHotExecutive({ d, inv, onMonthClick }) {
  return (
    <div className="space-y-5" data-testid="zonas-hot-executive">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <Trend d={d} onMonthClick={onMonthClick}/>
        <Ranking d={d}/>
      </div>
      <div className="surface p-5 border-l-4 border-orange-500" data-testid="zonas-hot-findings">
        <h2 className="text-base md:text-lg font-black font-display flex items-center gap-2 mb-2"><Lightbulb size={16} className="text-orange-600"/> Hallazgos</h2>
        <ul className="space-y-1.5">
          {findings(d, inv).map((t, i) => (
            <li key={i} className="text-[15px] md:text-base font-semibold text-gray-700 flex gap-2 leading-relaxed" data-testid={`zonas-hot-finding-${i}`}>
              <span className="text-orange-500 font-black">•</span><span>{t}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
